import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce';
import './_styles_/SubmarketDetailTab.css';
import useExtendedState from '../../hooks/useExtendedState';
import { submarketType } from '../../types';
import ApplyChangesButton from '../buttons/ApplyChangesButton';
import Privileges from '../../utils/Privileges';
import useMultiValidator from '../../hooks/useMultiValidator';
import useExtendedMutation from '../../hooks/useExtendedMutation';
import { Privileged } from '../../hocs/withPrivileges';
import {
    SUBMARKET_CODE_MAX_LENGTH,
    SUBMARKET_NAME_MAX_LENGTH,
    submarketCodeValidator,
    submarketNameValidator,
} from '../../validators/submarkets';
import {
    ALL_SUBMARKETS_QUERY_NAME,
    TOGGLE_SUBMARKET_ACTIVATION,
    UPDATE_SUBMARKET,
} from '../../graphql/submarkets';
import PrivilegedTextField from '../fields/privileged/PrivilegedTextField';
import ExpandedListTab from '../ExpandedListTab';
import Constants from '../../utils/Constants';
import ReadonlyField from '../fields/ReadonlyField';
import ActivationButton from '../buttons/ActivationButton';
import InfoPanel from '../InfoPanel';
import {
    transformSubmarketDeactivationBlockers,
    transformSubmarketModificationBlockers,
} from '../blockers/SubmarketBlockers';
import { canFieldBeModified } from '../../utils/fields';
import { compositeValidator, wafValidator } from '../../hooks/validationSupport';

SubmarketDetailTab.propTypes = {
    submarket: submarketType.isRequired,
};

function SubmarketDetailTab(props) {
    const {
        submarket,
    } = props;
    const { t } = useTranslation();
    const nameState = useExtendedState(submarket.name);
    const codeState = useExtendedState(submarket.code);
    const { validators, isValid } = useMultiValidator(true, {
        name: compositeValidator(submarketNameValidator(t), wafValidator(t)),
        code: compositeValidator(submarketCodeValidator(t), wafValidator(t)),
    });
    const isChanged = nameState.isChanged || codeState.isChanged;
    const isChangedPromise = isChanged ? Promise.resolve() : Promise.reject();

    const [isDisabled, setIsDisabled] = useState(true);
    Promise.all([isChangedPromise, isValid()])
        .then(() => setIsDisabled(false))
        .catch(() => setIsDisabled(true));

    useDebounce(!isValid || !isChanged, Constants.VALIDITY_DELAY_IN_MS);
    const statesToResetOnSuccess = [nameState, codeState];
    const updateSubmarketMutation = useExtendedMutation(UPDATE_SUBMARKET, {
        variables: {
            id: submarket.id,
            code: codeState.value,
            name: nameState.value,
        },
        statesToResetOnSuccess,
        successMessageOptions: { key: 'submarkets.notification.submarket-update-success', dataProperty: 'updateSubmarket' },
        failureMessageOptions: { key: 'submarkets.notification.submarket-update-failure', params: { name: submarket.name, code: submarket.code } },
        refetchQueries: [ALL_SUBMARKETS_QUERY_NAME],
    });
    const isActivationButtonDisabled = submarket.active && submarket.deactivationBlockers?.length > 0;
    let toggleSubmarketActivationConfirmMessageKey = 'submarkets.confirm-activate-submarket';
    if (submarket.active) {
        toggleSubmarketActivationConfirmMessageKey = 'submarkets.confirm-deactivate-submarket';
    }
    const toggleSubmarketActivationMutation = useExtendedMutation(TOGGLE_SUBMARKET_ACTIVATION, {
        variables: { id: submarket.id },
        statesToResetOnSuccess,
        successMessageCreator: ({ toggleSubmarketActivation: { active: updatedActive, name: updatedName, code: updatedCode } }) => {
            let toggleSubmarketActivationSuccessMessageKey = 'submarkets.notification.submarket-deactivate-success';
            if (updatedActive) {
                toggleSubmarketActivationSuccessMessageKey = 'submarkets.notification.submarket-activate-success';
            }
            return t(toggleSubmarketActivationSuccessMessageKey, { name: updatedName, code: updatedCode });
        },
        failureMessageCreator: () => {
            let toggleSubmarketActivationFailureMessageKey = 'submarkets.notification.submarket-activate-failure';
            if (submarket.active) {
                toggleSubmarketActivationFailureMessageKey = 'submarkets.notification.submarket-deactivate-failure';
            }
            return t(toggleSubmarketActivationFailureMessageKey, { name: submarket.name, code: submarket.code });
        },
        refetchQueries: [ALL_SUBMARKETS_QUERY_NAME],
    });
    return (
        <ExpandedListTab
            className="submarket-detail-tab"
            title={t('submarkets.submarket-detail')}
        >
            <div className="submarket-detail-tab--readonly-fields">
                <ReadonlyField
                    label={t('submarkets.active')}
                    value={submarket.active ? t('misc.yes') : t('misc.no')}
                    className="submarket-detail-tab--fields--active"
                />
            </div>
            <div className="rich-list-row-expanded--fields">
                <PrivilegedTextField
                    sufficientPrivileges={canFieldBeModified('name', submarket.modificationBlockers) ? [Privileges.SUBMARKET_EDIT] : []}
                    label={t('submarkets.name')}
                    state={nameState}
                    validator={validators.name}
                    className="submarket-detail-tab--fields--name wide"
                    placeholder={t('submarkets.placeholder.name')}
                    maxLength={SUBMARKET_NAME_MAX_LENGTH}
                    isRequired
                />
                <PrivilegedTextField
                    sufficientPrivileges={canFieldBeModified('code', submarket.modificationBlockers) ? [Privileges.SUBMARKET_EDIT] : []}
                    label={t('submarkets.code')}
                    state={codeState}
                    validator={validators.code}
                    className="submarket-detail-tab--fields--code"
                    placeholder={t('submarkets.placeholder.code')}
                    maxLength={SUBMARKET_CODE_MAX_LENGTH}
                    isRequired
                />
            </div>
            <Privileged sufficientPrivileges={[Privileges.SUBMARKET_EDIT]}>
                <div className="rich-list-row-expanded--buttons">
                    <div className="rich-list-row-expanded--buttons--left">
                        <ApplyChangesButton
                            extendedMutation={updateSubmarketMutation}
                            disabled={isDisabled}
                            confirmationMessage={t('submarkets.confirm-update-submarket', { name: submarket.name, code: submarket.code })}
                        />
                    </div>
                    <div className="rich-list-row-expanded--buttons--middle">
                        <ActivationButton
                            isActive={submarket.active}
                            disabled={isActivationButtonDisabled}
                            extendedMutation={toggleSubmarketActivationMutation}
                            confirmationMessage={t(toggleSubmarketActivationConfirmMessageKey, { name: submarket.name, code: submarket.code })}
                        />
                    </div>
                    <div className="rich-list-row-expanded--buttons--right" />
                </div>
                {submarket.deactivationBlockers?.map((deactivationBlocker) => (
                    <InfoPanel
                        key={deactivationBlocker}
                        message={transformSubmarketDeactivationBlockers(t, deactivationBlocker)}
                    />
                ))}
                {submarket?.modificationBlockers?.blockerConstants.map((modificationBlocker) => (
                    <InfoPanel
                        key={modificationBlocker}
                        message={transformSubmarketModificationBlockers(t, modificationBlocker)}
                    />
                ))}
            </Privileged>
        </ExpandedListTab>
    );
}

export default SubmarketDetailTab;
